import {
    AibackReportRatingTimelinePointEntity,
    AibackReportResponseTimeTimelinePointEntity,
} from '@/entities/aiback-report.entity';
import { ReportModalAspectRadarPoint } from '@/domain/reporting/reporting-modal-aggregations';
import {
    buildAspectRadarGeometry,
    buildRatingTimelineGeometry,
    buildTreemapLabelLayout,
} from '@/domain/reporting/reporting-shared.geometry';
import {
    REPORTING_RATING_AXIS_MAX,
    REPORTING_RATING_AXIS_MIN,
    REPORTING_RATING_DISTRIBUTION_LAYOUT,
} from '@/domain/reporting/reporting-shared.chart-presets';
import { buildRatingDistributionLayout } from '@/domain/reporting/reporting-shared.collections';
import { getTextColorByBackground, pickColorByRating } from '@/domain/reporting/reporting-shared.colors';
import { formatDateOnlyInGmtPlus3 } from '@/domain/reporting/reporting-shared.formatters';
import { buildNiceTickValues } from '@/domain/reporting/reporting-shared.math';
import {
    type AspectRadarGeometry,
    type RatingDistributionLayout,
    type TreemapLayoutItem,
} from '@/domain/reporting/reporting-shared.types';

export const REPORT_MODAL_RATING_CHART_DIMENSIONS = {
    width: 500,
    height: 116,
    paddingTop: 8,
    paddingRight: 10,
    paddingBottom: 22,
    paddingLeft: 30,
    axisMin: REPORTING_RATING_AXIS_MIN,
    axisMax: REPORTING_RATING_AXIS_MAX,
} as const;

export const REPORT_MODAL_RATING_DISTRIBUTION_DIMENSIONS = REPORTING_RATING_DISTRIBUTION_LAYOUT;

export const REPORT_MODAL_ASPECT_RADAR_DIMENSIONS = {
    width: 420,
    height: 300,
    centerX: 210,
    centerY: 150,
    radius: 100,
    gridLevels: 5,
} as const;

export type ReportModalAverageRatingChartData = {
    width: number;
    height: number;
    axisTop: number;
    axisLeft: number;
    axisBottom: number;
    axisRight: number;
    yTicks: Array<{
        value: number;
        y: number;
    }>;
    polyline: string;
    points: Array<{
        x: number;
        y: number;
    }>;
    firstLabel: string;
    lastLabel: string;
    firstLabelX: number;
    lastLabelX: number;
    xLabelsY: number;
};

export type ReportModalResponseTimeChartData = ReportModalAverageRatingChartData;

export type ReportModalRatingDistributionChartData = RatingDistributionLayout;

export type ReportModalTreemapTileViewModel = {
    id: string;
    title: string;
    left: string;
    top: string;
    width: string;
    height: string;
    backgroundColor: string;
    textColor: string;
    contentClassName: string;
    labelClassName: string;
    label: string | null;
};

export type ReportModalAspectRadarChartData = AspectRadarGeometry<ReportModalAspectRadarPoint>;

export const formatReportModalMinutesValue = (value: number | null): string => {
    if (value === null || Number.isNaN(value)) {
        return '—';
    }

    return value.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
    });
};

const buildReportModalTimelineChartData = (
    points: Array<{ date: string; value: number }>,
    options: {
        axisMin: number;
        axisMax: number;
        tickValues?: number[];
    },
): ReportModalAverageRatingChartData | null => {
    const geometry = buildRatingTimelineGeometry(
        points.map((point) => ({
            date: point.date,
            averageRating: point.value,
        })),
        {
            width: REPORT_MODAL_RATING_CHART_DIMENSIONS.width,
            height: REPORT_MODAL_RATING_CHART_DIMENSIONS.height,
            paddingTop: REPORT_MODAL_RATING_CHART_DIMENSIONS.paddingTop,
            paddingRight: REPORT_MODAL_RATING_CHART_DIMENSIONS.paddingRight,
            paddingBottom: REPORT_MODAL_RATING_CHART_DIMENSIONS.paddingBottom,
            paddingLeft: REPORT_MODAL_RATING_CHART_DIMENSIONS.paddingLeft,
            axisMin: options.axisMin,
            axisMax: options.axisMax,
            tickValues: options.tickValues,
        },
    );

    if (!geometry) {
        return null;
    }

    const polyline = geometry.points.map((point) => `${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ');
    const firstPoint = geometry.points[0];
    const lastPoint = geometry.points[geometry.points.length - 1];

    return {
        width: geometry.width,
        height: geometry.height,
        axisTop: REPORT_MODAL_RATING_CHART_DIMENSIONS.paddingTop,
        axisLeft: REPORT_MODAL_RATING_CHART_DIMENSIONS.paddingLeft,
        axisBottom: geometry.axisBottom,
        axisRight: geometry.axisRight,
        yTicks: geometry.yTicks,
        polyline,
        points: geometry.points,
        firstLabel: formatDateOnlyInGmtPlus3(firstPoint.date),
        lastLabel: formatDateOnlyInGmtPlus3(lastPoint.date),
        firstLabelX: firstPoint.x,
        lastLabelX: lastPoint.x,
        xLabelsY: REPORT_MODAL_RATING_CHART_DIMENSIONS.height - 7,
    };
};

export const buildReportModalAverageRatingChartData = (
    points: AibackReportRatingTimelinePointEntity[],
): ReportModalAverageRatingChartData | null =>
    buildReportModalTimelineChartData(
        points.map((point) => ({
            date: point.date,
            value: point.averageRating,
        })),
        {
            axisMin: REPORT_MODAL_RATING_CHART_DIMENSIONS.axisMin,
            axisMax: REPORT_MODAL_RATING_CHART_DIMENSIONS.axisMax,
        },
    );

export const buildReportModalResponseTimeChartData = (
    points: AibackReportResponseTimeTimelinePointEntity[],
): ReportModalResponseTimeChartData | null => {
    if (!points.length) {
        return null;
    }

    const { axisMax, tickValues } = buildNiceTickValues(
        Math.max(...points.map((point) => point.averageResponseTimeMinutes), 0),
    );

    return buildReportModalTimelineChartData(
        points.map((point) => ({
            date: point.date,
            value: point.averageResponseTimeMinutes,
        })),
        {
            axisMin: 0,
            axisMax,
            tickValues,
        },
    );
};

export const buildReportModalRatingDistributionChartData = (
    buckets: Array<{
        stars: number;
        value: number;
    }>,
): ReportModalRatingDistributionChartData | null =>
    buildRatingDistributionLayout(buckets, REPORT_MODAL_RATING_DISTRIBUTION_DIMENSIONS);

export const buildReportModalTreemapTileViewModel = (item: TreemapLayoutItem): ReportModalTreemapTileViewModel => {
    const backgroundColor = pickColorByRating(item.rating);
    const textColor = getTextColorByBackground(backgroundColor);
    const labelLayout = buildTreemapLabelLayout(
        {
            label: item.label,
            value: item.value,
            width: item.width,
            height: item.height,
        },
        {
            minArea: 80,
            minHeight: 7,
            minEstimatedLabelWidth: 22,
            charWidthMultiplier: 0.62,
            minVerticalFitHeight: 11,
            maxLabelLengthWhenHorizontalFit: 64,
            maxLabelLengthWhenNotHorizontalFit: 36,
        },
    );

    return {
        id: item.id,
        title: labelLayout.labelWithValue,
        left: `${item.x}%`,
        top: `${item.y}%`,
        width: `${item.width}%`,
        height: `${item.height}%`,
        backgroundColor,
        textColor,
        contentClassName: `flex h-full w-full p-1.5 ${labelLayout.fitsVertically ? 'items-center' : 'items-start'} ${
            labelLayout.fitsHorizontally ? 'justify-center' : 'justify-start'
        }`,
        labelClassName: `line-clamp-2 text-[11px] font-medium leading-tight ${
            labelLayout.fitsHorizontally ? 'text-center' : 'text-left'
        }`,
        label: labelLayout.label,
    };
};

export const buildReportModalAspectRadarChartData = (
    points: ReportModalAspectRadarPoint[],
): ReportModalAspectRadarChartData | null => buildAspectRadarGeometry(points, REPORT_MODAL_ASPECT_RADAR_DIMENSIONS);
